import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
  query,
  setSearchQuery,
  toggleShowFullForm,
  handleKeyDown,
  handleClickSearch,
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';
import { debounce } from 'lodash-es';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);

const QuickSearch = () => {
  const { query, result, showResult, showFullForm, selectedItem } = useSelector(
    (state) => state.quickSearch
  );
  const [lastClickedNode, setLastClickedNode] = useState(null);
  const searchUrl =
    window.__litium.quickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();
  const previousSelectedItem = usePrevious(selectedItem);
  const [navbarWidth, setNavbarWidth] = useState(0);
  const [navbarRight, setNavbarRight] = useState(0);
  const setSearchWidth = () => {
    const navbarDesktop = document.getElementById('navbar-desktop');
    if (navbarDesktop) {
      if (searchContainer.current) {
        const rect = searchContainer.current.getBoundingClientRect();
        var headerRowInner = document.querySelector('.header__row-inner');
        const distanceFromRightViewport =
          headerRowInner.offsetWidth -
          rect.left +
          headerRowInner.getBoundingClientRect().left;
        setNavbarRight(distanceFromRightViewport);
      }
    } else {
      const navbar = document.getElementById('navbar').offsetWidth;
      const locationEl = document.querySelector('.location').offsetWidth;

      setNavbarWidth(navbar + locationEl + 1);
    }
  };
  const handleClear = () => {
    if (searchInput.current) {
      searchInput.current.value = '';
      dispatch(setSearchQuery(''));
    }
  };
  const isInMobile = () => {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0
    );
    return vw < 1024;
  };
  const clickHandler = useCallback((event) => {
    if (isInMobile()) return;
    setLastClickedNode(event.target);
  }, []);
  const resizeHandler = useCallback((event) => {
    const forceShow = document.querySelector('.quick-search__form--force-show');
      setNavbarWidth();
      if (forceShow) dispatch(toggleShowFullForm());
    setLastClickedNode(event.target);
  }, []);
  useEffect(() => {
    // listen for click event to hide the search when clicking outside
    window.addEventListener('mousedown', clickHandler);
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('mousedown', clickHandler);
      window.removeEventListener('resize', resizeHandler);
    };
  }, [clickHandler, resizeHandler]);

  useEffect(() => {
    const headerElm = document.getElementById('header');
    const bodyEl = document.querySelector('body');
    const searchResultActiveClassName = 'search-result--active';
    if (bodyEl) {
      if (showFullForm) {
        bodyEl.classList.add(searchResultActiveClassName);
      } else {
        bodyEl.classList.remove(searchResultActiveClassName);
      }
      headerElm.classList.toggle('header--search-active', showFullForm);
    }
    if (showFullForm) {
      setSearchWidth();
    }
  }, [showFullForm]);

  useEffect(() => {
    // set query value if it is avaialble in the Url
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q')) {
      const query = urlParams.get('q');
      dispatch(setSearchQuery(query));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem !== previousSelectedItem) {
      const el = document.querySelector('.quick-search-result__item--selected');
      el &&
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
    }
  }, [selectedItem, previousSelectedItem]);

  return (
    <div className="quick-search" role="search" ref={searchContainer}>
      <a
        className={`quick-search__link--block ${showFullForm ? 'active' : ''}`}
        rel="nofollow"
        href="#"
        title={translate('general.search')}
        onClick={(e) => {
          e.preventDefault();
          dispatch(toggleShowFullForm());
          setTimeout(() => {
            searchInput.current && searchInput.current.focus();
          }, 0);
        }}
      >
        {showFullForm ? (
          <i className="quick-search__icon--clear"></i>
        ) : (
          <i className="quick-search__icon"></i>
        )}
        <svg
          className="quick-search__link-icon"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.1634 16.8367L14.4533 13.125C15.5657 11.6754 16.085 9.85687 15.906 8.03839C15.7269 6.21991 14.8629 4.53763 13.4891 3.3328C12.1153 2.12798 10.3346 1.49082 8.50834 1.55059C6.68205 1.61036 4.94687 2.36258 3.65479 3.65466C2.36272 4.94673 1.6105 6.68191 1.55073 8.50821C1.49096 10.3345 2.12811 12.1151 3.33294 13.4889C4.53777 14.8627 6.22005 15.7268 8.03853 15.9059C9.85701 16.0849 11.6755 15.5655 13.1251 14.4531L16.8384 18.1672C16.9256 18.2544 17.0292 18.3236 17.1431 18.3708C17.257 18.418 17.3792 18.4423 17.5025 18.4423C17.6258 18.4423 17.7479 18.418 17.8619 18.3708C17.9758 18.3236 18.0793 18.2544 18.1665 18.1672C18.2537 18.08 18.3229 17.9765 18.3701 17.8625C18.4173 17.7486 18.4416 17.6265 18.4416 17.5031C18.4416 17.3798 18.4173 17.2577 18.3701 17.1437C18.3229 17.0298 18.2537 16.9263 18.1665 16.8391L18.1634 16.8367ZM3.43764 8.75C3.43764 7.69929 3.74921 6.67217 4.33295 5.79853C4.9167 4.9249 5.7464 4.24398 6.71713 3.84189C7.68786 3.4398 8.75603 3.33459 9.78656 3.53958C10.8171 3.74456 11.7637 4.25053 12.5066 4.9935C13.2496 5.73646 13.7556 6.68306 13.9606 7.71358C14.1655 8.74411 14.0603 9.81227 13.6582 10.783C13.2562 11.7537 12.5752 12.5834 11.7016 13.1672C10.828 13.7509 9.80085 14.0625 8.75014 14.0625C7.34162 14.0611 5.9912 13.5009 4.99523 12.5049C3.99926 11.5089 3.43908 10.1585 3.43764 8.75Z"
            fill="currentColor"
          />
        </svg>

        <span className="quick-search__title hide">
          {translate('general.search')}
        </span>
      </a>
      <div
        className={`quick-search__form ${
          showFullForm ? 'quick-search__form--force-show' : ''
        }`}
        style={{
          minWidth: `${navbarWidth}px`,
          right: navbarRight === 0 ? undefined : `${navbarRight}px`,
        }}
        role="search"
      >
        <div className="quick-search__icon--container">
          <i
            className="quick-search__icon--search"
            onClick={(e) => dispatch(toggleShowFullForm())}
          ></i>
        </div>
        <input
          className="quick-search__input"
          type="search"
          placeholder={translate('general.search')}
          autoComplete="off"
          value={decodeURIComponent(query)}
          onChange={(event) => {
            const text = encodeURIComponent(event.target.value);
            dispatch(setSearchQuery(text));
            debouncedQuery(dispatch, text);
          }}
          onKeyDown={(event) => dispatch(handleKeyDown(event, { searchUrl }))}
          ref={searchInput}
          onBlur={() => {
            if (isInMobile()) return null;
            if (
              searchContainer.current &&
              !searchContainer.current.contains(lastClickedNode)
            ) {
              showFullForm && dispatch(toggleShowFullForm());
            }
          }}
        />
        <div className="quick-search__clearText" onClick={handleClear}>
          CLEAR
        </div>
        <button
          className="quick-search__submit-button"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            dispatch(handleClickSearch({ searchUrl }));
          }}
        >
          <i className="quick-search__submit-icon"></i>
          <span className="quick-search__submit-title">
            {translate('general.search')}
          </span>
        </button>
        {showResult && query !== '' && (
          <QuickSearchResult
            result={result}
            selectedItem={selectedItem}
            searchUrl={searchUrl}
          />
        )}
      </div>
    </div>
  );
};

export default QuickSearch;
